import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../api/client';

// ─── Design tokens ───────────────────────────────────────────────────────────
const C = {
  bg: 'var(--bs-bg, #0a0a0a)',
  card: 'var(--bs-surface, #111)',
  border: 'var(--bs-border, #1e1e1e)',
  green: 'var(--bs-green, #00e676)',
  red: 'var(--bs-red, #ff1744)',
  amber: 'var(--bs-amber, #ffab00)',
  gold: '#FFD700',
  silver: '#C0C0C0',
  bronze: '#CD7F32',
  dim: 'var(--bs-muted, #555)',
  dimmer: 'var(--bs-muted, #444)',
  dimmest: 'var(--bs-border, #333)',
  text: 'var(--bs-ink, #e0e0e0)',
};

const MEDAL = { 1: C.gold, 2: C.silver, 3: C.bronze };
const medalColor = (rank) => MEDAL[rank] || null;

// ─── Local primitives ─────────────────────────────────────────────────────────
const Card = ({ children, style }) => (
  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, ...style }}>
    {children}
  </div>
);

const Label = ({ children }) => (
  <span style={{ fontSize: 10, fontFamily: 'JetBrains Mono', color: C.dim, textTransform: 'uppercase', letterSpacing: 1 }}>
    {children}
  </span>
);

const Pill = ({ active, onClick, children }) => (
  <button onClick={onClick} style={{
    background: active ? C.green : 'var(--bs-border, #1a1a1a)',
    color: active ? 'var(--bs-bg, #000)' : 'var(--bs-muted, #666)',
    border: 'none', borderRadius: 20, padding: '6px 16px',
    fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'JetBrains Mono',
    transition: 'all 0.15s',
  }}>
    {children}
  </button>
);

const Skeleton = ({ w = '100%', h = 16, style }) => (
  <div style={{ width: w, height: h, background: 'var(--bs-border, #1a1a1a)', borderRadius: 6, animation: 'pulse 1.5s ease-in-out infinite', flexShrink: 0, ...style }} />
);

// ─── Sparkline (dots + dashed line) ─────────────────────────────────────────
const UP_POINTS   = [[5,16],[18,12],[32,14],[45,8],[58,10],[72,5],[86,7],[100,3]];
const DOWN_POINTS = [[5,4],[18,7],[32,5],[45,10],[58,8],[72,13],[86,11],[100,16]];

function Sparkline({ positive }) {
  const pts = positive ? UP_POINTS : DOWN_POINTS;
  const color = positive ? C.green : C.red;
  const polyline = pts.map(([x, y]) => `${x},${y}`).join(' ');

  return (
    <svg viewBox="0 0 100 20" width={58} height={18} preserveAspectRatio="none" style={{ display: 'block' }}>
      <polyline
        points={polyline}
        fill="none"
        stroke={color}
        strokeWidth={1}
        strokeDasharray="1 3"
        opacity={0.3}
      />
      {pts.map(([x, y], i) => {
        const opacity = 0.4 + (i / (pts.length - 1)) * 0.6;
        const isLast = i === pts.length - 1;
        return isLast
          ? <circle key={i} cx={x} cy={y} r={2.5} fill={color} opacity={opacity} />
          : <circle key={i} cx={x} cy={y} r={1.5} fill="none" stroke={color} strokeWidth={1} opacity={opacity} />;
      })}
    </svg>
  );
}

// ─── Rank medal/number display ────────────────────────────────────────────────
function RankNum({ rank }) {
  const color = medalColor(rank) || '#2a2a2a';
  return (
    <div style={{ width: 18, textAlign: 'center', flexShrink: 0, fontSize: 11, fontFamily: 'JetBrains Mono', fontWeight: 700, color }}>
      {rank}
    </div>
  );
}

// ─── My rank tile ─────────────────────────────────────────────────────────────
function MyRankTile({ label, rank, total }) {
  const color = rank === 1 ? C.gold : rank === 2 ? C.silver : rank === 3 ? C.bronze : C.green;
  return (
    <div style={{ flex: 1, textAlign: 'center', padding: '8px 4px' }}>
      <div style={{ fontSize: 8, fontFamily: 'JetBrains Mono', color: C.dim, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontFamily: 'Dela Gothic One', color: rank ? color : '#2a2a2a', lineHeight: 1 }}>
        {rank ? `#${rank}` : '—'}
      </div>
      <div style={{ fontSize: 8, fontFamily: 'JetBrains Mono', color: '#444', marginTop: 3 }}>
        {rank ? `of ${total}` : 'unranked'}
      </div>
    </div>
  );
}

// ─── Primary stat display ─────────────────────────────────────────────────────
function PrimaryStat({ row, sort }) {
  if (sort === 'pnl') {
    const pos = row.pnl >= 0;
    return (
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 14, fontFamily: 'JetBrains Mono', fontWeight: 700, color: pos ? C.green : C.red }}>
          {pos ? '+' : ''}{row.pnl.toFixed(0)}
        </div>
        <Sparkline positive={pos} />
      </div>
    );
  }
  if (sort === 'winrate') {
    const wr = row.win_rate || 0;
    const color = wr >= 60 ? C.green : wr >= 45 ? C.amber : C.red;
    return (
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 14, fontFamily: 'JetBrains Mono', fontWeight: 700, color }}>{wr}%</div>
        <Sparkline positive={wr >= 50} />
      </div>
    );
  }
  // wagered
  return (
    <div style={{ textAlign: 'right', flexShrink: 0 }}>
      <div style={{ fontSize: 14, fontFamily: 'JetBrains Mono', fontWeight: 700, color: C.amber }}>
        {row.total_wagered.toFixed(0)}
      </div>
      <Sparkline positive={row.pnl >= 0} />
    </div>
  );
}

// ─── Secondary stats line ────────────────────────────────────────────────────
function SecondaryStats({ row, sort }) {
  if (sort === 'pnl') {
    return <span>{row.win_rate}% win · {row.bets_placed} bets</span>;
  }
  if (sort === 'winrate') {
    const pnl = row.pnl;
    return <span>{pnl >= 0 ? '+' : ''}{pnl.toFixed(0)} P&L · {row.bets_placed} bets</span>;
  }
  return <span>{row.win_rate}% win · {row.bets_placed} bets</span>;
}

// ─── Specialist badge mapping ─────────────────────────────────────────────────
const SPECIALIST = {
  Evidence:    { label: 'Fact Checker',        color: '#2196f3' },
  Sources:     { label: 'Source Hound',        color: '#9c27b0' },
  Headlines:   { label: 'Headline Hunter',     color: '#e91e63' },
  Confidence:  { label: 'Confidence Analyst',  color: '#ff9800' },
  Corrections: { label: 'Error Tracker',       color: '#795548' },
  Composite:   { label: 'Truth Seeker',        color: '#00e676' },
};

// ─── Individual leaderboard row ───────────────────────────────────────────────
function LBRow({ row, sort, isMe, index }) {
  const mc = medalColor(row.rank);
  const accentColor = isMe ? C.green : (mc || C.border);
  const rowBg    = isMe ? 'var(--bs-green-bg, #00e67608)' : C.card;
  const rowBorder = isMe ? `1px solid var(--bs-green-bd, #00e67644)` : `1px solid ${C.border}`;
  const nameColor = isMe ? C.green : C.text;

  return (
    <div style={{
      display: 'flex', alignItems: 'stretch', overflow: 'hidden',
      borderRadius: 12, border: rowBorder, background: rowBg,
      marginBottom: 8,
      animation: `fadeUp 0.3s ease both`,
      animationDelay: `${index * 40}ms`,
    }}>
      {/* Left accent bar */}
      <div style={{ width: 3, flexShrink: 0, background: accentColor }} />

      {/* Row content */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px' }}>
        <RankNum rank={row.rank} />

        {/* Identity */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: nameColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.display_name || row.username}
            </span>
            {isMe && (
              <span style={{ fontSize: 9, fontFamily: 'JetBrains Mono', color: C.green, background: 'var(--bs-green-bg, #00e67622)', borderRadius: 3, padding: '1px 5px', flexShrink: 0 }}>
                YOU
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {row.top_criteria && SPECIALIST[row.top_criteria] && (
              <span style={{ fontSize: 9, fontFamily: 'JetBrains Mono', color: SPECIALIST[row.top_criteria].color, background: SPECIALIST[row.top_criteria].color + '26', borderRadius: 10, padding: '1px 7px', flexShrink: 0 }}>
                {SPECIALIST[row.top_criteria].label}
              </span>
            )}
            <span style={{ fontSize: 9, fontFamily: 'JetBrains Mono', color: C.dimmer }}>
              <SecondaryStats row={row} sort={sort} />
            </span>
          </div>
        </div>

        <PrimaryStat row={row} sort={sort} />
      </div>
    </div>
  );
}

// ─── Skeleton row ─────────────────────────────────────────────────────────────
function SkeletonRow() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, marginBottom: 8 }}>
      <Skeleton w={32} h={32} style={{ borderRadius: '50%' }} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <Skeleton w="40%" h={14} />
        <Skeleton w="60%" h={10} />
      </div>
      <Skeleton w={60} h={24} />
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const SORT_TABS = [
  { id: 'pnl',     label: 'P&L' },
  { id: 'winrate', label: 'WIN RATE' },
  { id: 'wagered', label: 'MOST WAGERED' },
];

export default function RanksPage({ user, setPage }) {
  const [data, setData]       = useState([]);       // full dataset (all sorts)
  const [sort, setSort]       = useState('pnl');
  const [loading, setLoading] = useState(true);
  const [myRanks, setMyRanks] = useState(null);
  const [myRanksLoading, setMyRanksLoading] = useState(false);
  const pollRef = useRef(null);

  // Client-side sorted view
  const sorted = (() => {
    if (!data.length) return [];
    if (sort === 'winrate') {
      return [...data]
        .filter(r => r.bets_placed >= 5)
        .sort((a, b) => b.win_rate - a.win_rate)
        .map((r, i) => ({ ...r, rank: i + 1 }));
    }
    if (sort === 'wagered') {
      return [...data]
        .sort((a, b) => b.total_wagered - a.total_wagered)
        .map((r, i) => ({ ...r, rank: i + 1 }));
    }
    // pnl (already sorted from server, but re-sort for safety)
    return [...data]
      .sort((a, b) => b.pnl - a.pnl)
      .map((r, i) => ({ ...r, rank: i + 1 }));
  })();

  const fetchLeaderboard = useCallback(async () => {
    try {
      const res = await apiFetch('/api/leaderboard?sort=pnl&limit=50');
      const json = await res.json();
      if (json.leaderboard) setData(json.leaderboard);
    } catch (e) {
      // silent — keep stale data
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyRanks = useCallback(async () => {
    if (!user) return;
    setMyRanksLoading(true);
    try {
      const res = await apiFetch('/api/leaderboard/me');
      const json = await res.json();
      setMyRanks(json);
    } catch (e) {
      // silent
    } finally {
      setMyRanksLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchLeaderboard();
    fetchMyRanks();

    // Poll every 60s
    pollRef.current = setInterval(() => {
      fetchLeaderboard();
      if (user) fetchMyRanks();
    }, 60000);

    return () => clearInterval(pollRef.current);
  }, [fetchLeaderboard, fetchMyRanks, user]);

  const hasNoData = !loading && sorted.length === 0;
  const totalPlayers = myRanks?.total_players ?? data.length;

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '0 12px 32px' }}>

      {/* ── Header ── */}
      <div style={{ padding: '20px 0 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <div style={{ fontFamily: 'Dela Gothic One', fontSize: 24, color: C.text, letterSpacing: 1 }}>
            LEADERBOARD
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'var(--bs-border, #1a1a1a)', borderRadius: 20, padding: '3px 10px' }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.red, animation: 'pulse 2s ease-in-out infinite' }} />
            <span style={{ fontSize: 9, fontFamily: 'JetBrains Mono', fontWeight: 700, color: C.red, letterSpacing: 1 }}>LIVE</span>
          </div>
        </div>
        <div style={{ fontSize: 13, color: 'var(--bs-muted, #666)' }}>Top bettors by credibility intelligence</div>
      </div>

      {/* ── My Rank Banner (logged in only) ── */}
      {user && (
        <Card style={{ marginBottom: 16 }}>
          <Label>Your Ranking</Label>
          {myRanksLoading ? (
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <Skeleton h={52} style={{ flex: 1 }} />
              <Skeleton h={52} style={{ flex: 1 }} />
              <Skeleton h={52} style={{ flex: 1 }} />
            </div>
          ) : myRanks && (myRanks.pnl_rank || myRanks.winrate_rank || myRanks.wagered_rank) ? (
            <div style={{ display: 'flex', gap: 4, marginTop: 10 }}>
              <MyRankTile label="P&L"     rank={myRanks.pnl_rank}     total={totalPlayers} />
              <div style={{ width: 1, background: C.border, alignSelf: 'stretch' }} />
              <MyRankTile label="Win Rate" rank={myRanks.winrate_rank} total={totalPlayers} />
              <div style={{ width: 1, background: C.border, alignSelf: 'stretch' }} />
              <MyRankTile label="Wagered"  rank={myRanks.wagered_rank}  total={totalPlayers} />
            </div>
          ) : (
            <div style={{ fontSize: 12, color: C.dim, marginTop: 10, fontFamily: 'JetBrains Mono' }}>
              Place your first bet to appear on the leaderboard
            </div>
          )}
        </Card>
      )}

      {/* ── Sort tabs ── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {SORT_TABS.map(t => (
          <Pill key={t.id} active={sort === t.id} onClick={() => setSort(t.id)}>
            {t.label}
          </Pill>
        ))}
        {!loading && sorted.length > 0 && (
          <span style={{ fontSize: 11, color: C.dim, fontFamily: 'JetBrains Mono', alignSelf: 'center', marginLeft: 'auto' }}>
            {sorted.length} player{sorted.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* ── Rows ── */}
      {loading ? (
        <>
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </>
      ) : hasNoData ? (
        /* ── Empty state ── */
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🏆</div>
          <div style={{ fontSize: 15, color: 'var(--bs-muted, #666)', marginBottom: 20 }}>
            No bettors yet — be the first
          </div>
          <button
            onClick={() => setPage('markets')}
            style={{ background: C.green, color: 'var(--bs-bg, #000)', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 700, cursor: 'pointer', fontSize: 13, fontFamily: 'JetBrains Mono' }}
          >
            BROWSE MARKETS →
          </button>
        </div>
      ) : (
        sorted.map((row, i) => (
          <LBRow
            key={row.id}
            row={row}
            sort={sort}
            isMe={user && row.id === user.id}
            index={i}
          />
        ))
      )}

      {/* ── Footer note ── */}
      {!loading && sorted.length > 0 && (
        <div style={{ textAlign: 'center', fontSize: 9, fontFamily: 'JetBrains Mono', color: C.dimmest, marginTop: 20 }}>
          Rankings update after each market resolves · Min 1 bet to appear
        </div>
      )}
    </div>
  );
}
